import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { SITE } from "@/constants/testIds";
import { apiClient } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ReportCard } from "@/components/site/ReportCard";

// Hoisted outside the component so framer-motion receives a stable object
// reference across re-renders (e.g. while the user types in the search box).
const HEADER_MOTION = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function SectionPage() {
  const { sectionKey } = useParams();
  const section = SECTION_MAP[sectionKey];
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (section) document.title = `${section.label} | KIRA Score`;
    return () => { document.title = "KIRA Score — Research Before Returns"; };
  }, [section]);

  useEffect(() => {
    if (!section) return;
    setLoaded(false);
    const t = setTimeout(() => {
      apiClient.get("/public/reports", { params: { section: sectionKey, search: search.trim() || undefined, limit: 200 } })
        .then(({ data }) => setReports(data))
        .catch(() => {})
        .finally(() => setLoaded(true));
    }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [sectionKey, search, section]);

  if (!section) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-kira-bg text-white">
      <SiteNav />
      <div data-testid={SITE.sectionPage} className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <motion.div {...HEADER_MOTION}>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-kira-gold/10 border border-kira-gold/30">
              <section.icon className="h-5 w-5 text-kira-gold" />
            </div>
            <h1 className="font-chivo text-4xl sm:text-5xl font-bold">{section.label}</h1>
          </div>
          <p className="text-kira-textMuted max-w-2xl mb-8">{section.blurb}</p>
        </motion.div>

        <div className="relative max-w-md mb-8">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-kira-textMuted" />
          <input
            data-testid={SITE.sectionSearchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, symbol, sector..."
            className="w-full rounded-md border border-kira-border bg-kira-surface pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-kira-textMuted focus:border-kira-gold focus:outline-none"
          />
        </div>

        {loaded && reports.length === 0 ? (
          <div className="rounded-lg border border-dashed border-kira-border py-20 text-center">
            <p className="text-sm text-kira-textMuted">
              {search ? `No research matches "${search}".` : "Research for this section is being prepared. Check back soon."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {reports.map((r) => <ReportCard key={r.id} report={r} />)}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
